'use client';

import Link from '@eliadmin/core/src/components/link';
import { Button } from '@eliadmin/core/src/components/ui/button';
import {
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  useDialogStore,
} from '@eliadmin/core/src/components/ui/dialog';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { usePathname } from 'next/navigation';
import { useEffect, useRef } from 'react';
import { isCurriculumCompleted } from '../../utils/curriculum-utils';

const CURRICULUM_REMINDER_DIALOG_ID = 'CURRICULUM_REMINDER_DIALOG';
const CURRICULUM_REMINDER_STORAGE_PREFIX = 'jobinterview:curriculum-reminder:last-shown';

function getLocalDateKey() {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

function isReminderExcludedPath(pathname: string) {
  const excludedPaths = [ROUTES.REGISTER(), ROUTES.SIGN_IN()].map(path => path.split('?')[0]);

  return excludedPaths.some(path => pathname === path || pathname.endsWith(path));
}

function CurriculumReminderDialog() {
  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Dokončite si svoj profesijný životopis</DialogTitle>
        <DialogDescription>
          Hotový životopis vám pomôže reagovať na pracovné ponuky a zvýši vašu šancu, že si vás
          zamestnávatelia všimnú.
        </DialogDescription>
      </DialogHeader>
      <DialogFooter className="gap-2 sm:space-x-0">
        <DialogClose asChild>
          <Button type="button" variant="outline" className="hover:text-white">
            Neskôr
          </Button>
        </DialogClose>
        <Button asChild>
          <Link href={ROUTES.CURRICULUM_CREATE()}>Pokračovať v tvorbe</Link>
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

export function CurriculumReminderController() {
  const pathname = usePathname();
  const { me, isInitialized } = useCustomerStore(state => state);
  const openDialog = useDialogStore(state => state.open);
  const checkedReminderRef = useRef<string | null>(null);

  useEffect(() => {
    const customer = me?.customer;

    if (
      !isInitialized ||
      !customer ||
      customer.customer_type?.type !== 'student' ||
      isCurriculumCompleted(customer) ||
      isReminderExcludedPath(pathname)
    ) {
      return;
    }

    const today = getLocalDateKey();
    const reminderKey = `${customer.id}:${today}`;

    if (checkedReminderRef.current === reminderKey) return;
    checkedReminderRef.current = reminderKey;

    const storageKey = `${CURRICULUM_REMINDER_STORAGE_PREFIX}:${customer.id}`;

    try {
      if (window.localStorage.getItem(storageKey) === today) return;
      window.localStorage.setItem(storageKey, today);
    } catch {
      // The reminder still opens once in this app session when storage is unavailable.
    }

    openDialog({
      id: CURRICULUM_REMINDER_DIALOG_ID,
      content: <CurriculumReminderDialog />,
    });
  }, [isInitialized, me, openDialog, pathname]);

  return null;
}
